/**
 * Native Push Bridge — integração com push nativo no Android
 *
 * CRÍTICO: window.setNativePushToken e window.setNativeFcmToken ficam disponíveis
 * imediatamente ao importar este módulo. O app nativo pode chamar essas funções
 * antes do usuário estar logado.
 *
 * Persistência: token salvo em localStorage → botão fica verde mesmo após reload.
 */

import { API_URL } from '../config/api';
import { auth } from './firebase';

const LS_KEY = 'expoPushToken_v1';

// Token aguardando registro (recebido antes do login)
let _pendingToken = null;
let _registered = false;

async function registerToken(token) {
  if (!token) return false;
  try {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      console.log('📱 [NativePush] Sem usuário logado — token guardado para depois');
      _pendingToken = token;
      return false;
    }

    const idToken = await currentUser.getIdToken();
    const res = await fetch(`${API_URL}/push-token`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${idToken}`
      },
      body: JSON.stringify({ token })
    });

    if (res.ok) {
      console.log('✅ [NativePush] Token registrado no backend com sucesso');
      _pendingToken = null;
      // Persiste no localStorage para sobreviver reloads
      try { localStorage.setItem(LS_KEY, token); } catch(e) {}
      // Dispara evento de confirmação de registro
      window.dispatchEvent(new CustomEvent('nativePushTokenRegistered', { detail: { token } }));
      return true;
    } else {
      const body = await res.text().catch(() => '');
      console.warn('⚠️ [NativePush] Erro ao registrar token:', res.status, body);
      _pendingToken = token;
      return false;
    }
  } catch (err) {
    console.warn('⚠️ [NativePush] Falha ao enviar token para backend:', err.message);
    _pendingToken = token;
    return false;
  }
}

async function registerFcmToken(token) {
  if (!token) return false;
  try {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      console.log('📱 [NativePush] Sem usuário logado — FCM guardado para depois');
      _pendingToken = token;
      return false;
    }

    const idToken = await currentUser.getIdToken();
    const res = await fetch(`${API_URL}/push/fcm-token`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${idToken}`
      },
      body: JSON.stringify({ token })
    });

    if (res.ok) {
      console.log('✅ [NativePush] FCM token registrado no backend com sucesso');
      _pendingToken = null;
      try { localStorage.setItem(LS_KEY, token); } catch(e) {}
      window.dispatchEvent(new CustomEvent('nativePushTokenReceived', { detail: { token, kind: 'fcm' } }));
      return true;
    }

    const body = await res.text().catch(() => '');
    console.warn('⚠️ [NativePush] Erro ao registrar FCM token:', res.status, body);
    _pendingToken = token;
    return false;
  } catch (err) {
    console.warn('⚠️ [NativePush] Falha ao enviar FCM token para backend:', err.message);
    _pendingToken = token;
    return false;
  }
}

// ─── RESTAURAR TOKEN PERSISTIDO (antes de qualquer render) ────────────────────
try {
  const saved = localStorage.getItem(LS_KEY);
  if (saved) {
    window.__nativePushToken = saved;
    console.log('📱 [NativePush] Token restaurado do localStorage:', saved.substring(0, 20) + '...');
  }
} catch(e) {}
// ──────────────────────────────────────────────────────────────────────────────

// ─── CONFIGURAÇÃO IMEDIATA (na carga do módulo) ───────────────────────────────
function _onTokenReceived(token) {
  if (!token || typeof token !== 'string' || token.trim().length === 0) {
    console.warn('📱 [NativePush] Token inválido recebido:', token);
    return;
  }
  const looksLikeExpo = token.startsWith('ExponentPushToken') || token.startsWith('ExpoPushToken');
  const looksLikeFcm = token.length > 80 && !looksLikeExpo;
  if (!looksLikeExpo && !looksLikeFcm) {
    console.warn('📱 [NativePush] Token com formato inesperado (aceitando mesmo assim):', token.substring(0, 40));
  }
  console.log('📱 [NativePush] Token recebido:', token.substring(0, 40) + '...');

  window.__nativePushToken = token;
  _pendingToken = token;
  try { localStorage.setItem(LS_KEY, token); } catch(e) {}

  window.dispatchEvent(new CustomEvent('nativePushTokenReceived', { detail: { token, kind: looksLikeFcm ? 'fcm' : 'expo' } }));
  registerToken(token);
  if (looksLikeFcm) registerFcmToken(token);
}

// Método 1: Expo chama window.setNativePushToken(token) via injectJavaScript
// Múltiplos aliases para compatibilidade com diferentes versões do Expo / diferentes implementações nativas
window.setNativePushToken       = _onTokenReceived; // nome principal (documentado)
window.setExpoPushToken         = _onTokenReceived; // alias alternativo
window.receiveNativePushToken   = _onTokenReceived; // alias alternativo
window.onNativePushToken        = _onTokenReceived; // alias alternativo
window.setNativeFcmToken        = registerFcmToken;
window.setFcmPushToken          = registerFcmToken;

// Helper de debug: retorna o estado atual do bridge (use no console do Expo)
window.__nativePushBridgeDebug = () => ({
  hasToken: !!window.__nativePushToken,
  token: window.__nativePushToken ? window.__nativePushToken.substring(0, 40) + '...' : null,
  setNativePushToken: typeof window.setNativePushToken,
  bridge: 'v2'
});

// Método 2: Expo envia postMessage nativo (deprecated API ou custom)
// Escuta em window E document pois diferentes versões do React Native WebView usam diferentes alvos
function _handleNativeMessage(e) {
  try {
    const data = typeof e.data === 'string' ? JSON.parse(e.data) : e.data;
    if (!data || typeof data !== 'object') return;
    // Suporte a vários formatos que o app nativo pode usar
    const token = data.token || data.pushToken || data.expoPushToken || data.expoToken || data.nativePushToken;
    const type = data.type || data.event || '';
    if (type === 'EXPO_PUSH_TOKEN' && token) {
      // Token Expo (ExponentPushToken) — salva APENAS no endpoint Expo, NÃO no FCM
      console.log('📱 [NativePush] Token Expo recebido via postMessage');
      _onTokenReceived(token);
      return;
    }
    if (type === 'FCM_TOKEN' && token) {
      const looksLikeExpo = typeof token === 'string' && (token.startsWith('ExponentPushToken') ||
        token.startsWith('ExpoPushToken'));
      if (looksLikeExpo) {
        // ExponentPushToken enviado como FCM_TOKEN (versões antigas do app) — tratar como Expo
        console.log('📱 [NativePush] ExponentPushToken recebido como FCM_TOKEN — redirecionando para Expo endpoint');
        _onTokenReceived(token);
      } else {
        console.log('📱 [NativePush] Token FCM real recebido via postMessage');
        _onTokenReceived(token);
        registerFcmToken(token);
      }
      return;
    }
    if ((type === 'REQUEST_FCM_TOKEN' || type === 'FCM_PUSH_TOKEN') && token) {
      console.log('📱 [NativePush] Token nativo via postMessage (type:', type, ')');
      _onTokenReceived(token);
      return;
    }
    // Aceita mensagens com token Expo, independente do tipo (mais permissivo)
    if (token) {
      const tokenLike = typeof token === 'string' && (
        token.startsWith('ExponentPushToken') ||
        token.startsWith('ExpoPushToken') ||
        type === 'PUSH_TOKEN' || type === 'EXPO_TOKEN' || type === 'pushToken' || type === 'nativePushToken'
      );
      if (tokenLike) {
        console.log('📱 [NativePush] Token recebido via postMessage (type:', type, ')');
        _onTokenReceived(token);
      }
    }
  } catch(e) { /* JSON inválido — ignora */ }
}
window.addEventListener('message', _handleNativeMessage);
try { document.addEventListener('message', _handleNativeMessage); } catch(e) {}
// ──────────────────────────────────────────────────────────────────────────────

/**
 * Detecta se está rodando dentro do app nativo (Expo WebView)
 */
export function isNativeApp() {
  if (window.__nativePushToken) return true;
  const ua = navigator.userAgent || '';
  return ua.includes('ExpoWebView') || ua.includes('CalisteniaPremiadaApp');
}

/**
 * Inicializa o bridge após login.
 * Registra qualquer token pendente que chegou antes do usuário logar.
 * Também re-registra o token do localStorage caso não tenha sido registrado ainda.
 */
export function initNativePushBridge() {
  if (_registered) return;
  _registered = true;

  // Prioridade: token recém-recebido > token do localStorage
  const token = _pendingToken || window.__nativePushToken;
  if (token) {
    console.log('📱 [NativePush] initNativePushBridge: registrando token no backend');
    registerToken(token);
  }
}

/**
 * Abre uma URL no navegador externo de dentro do Expo WebView.
 */
export function openExternalURL(url) {
  if (window.ReactNativeWebView) {
    window.ReactNativeWebView.postMessage(JSON.stringify({ type: 'openExternalURL', url }));
    return;
  }
  window.open(url, '_blank', 'noopener,noreferrer');
}

/**
 * Limpa token local (usado no logout).
 */
export function resetNativePushBridge() {
  _registered = false;
  _pendingToken = null;
  try { localStorage.removeItem(LS_KEY); } catch(e) {}
  window.__nativePushToken = undefined;
}
